import { useState } from 'react';
import { Link } from 'react-router-dom';
import { NFT } from '@/types';
import { FiExternalLink, FiShoppingCart, FiInfo } from 'react-icons/fi';
import { formatAddress } from '@/utils/formatters';
import { motion } from 'framer-motion';

interface NFTCardProps {
  nft: NFT;
  onBuy?: () => void;
  isOwned?: boolean;
  isListed?: boolean;
  price?: string;
}

const NFTCard = ({ nft, onBuy, isOwned = false, isListed = false, price }: NFTCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  // Extract image URL from metadata
  const imageUrl = nft.metadata?.image
    ? nft.metadata.image.replace('ipfs://', 'https://ipfs.io/ipfs/')
    : 'https://via.placeholder.com/300';

  return (
    <Link
      to={`/nft/${nft.id}`}
      className="block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div
        className="bg-dark-800 rounded-xl overflow-hidden transition-all duration-300 shadow-3d hover:shadow-3d-hover"
        whileHover={{ y: -8 }} // Added vertical lift on hover
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* NFT Image */}
        <div className="relative aspect-square overflow-hidden">
          <img
            src={imageUrl}
            alt={nft.metadata?.name || 'NFT'}
            className="w-full h-full object-cover transition-transform duration-300"
            style={{
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            }}
          />
          
          {/* Overlay with actions on hover */}
          <div
            className={`absolute inset-0 bg-gradient-to-t from-dark-900/90 to-transparent flex items-end justify-between p-4 transition-opacity duration-300 z-10 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="shadow-3d rounded-lg">
              <Link
                to={`/nft/${nft.id}`}
                className="btn-primary py-1 px-3 text-sm"
              >
                <FiInfo className="mr-1 inline" /> Details
              </Link>
            </motion.div>
            
            {isListed && !isOwned && (
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="shadow-3d rounded-lg">
                <button
                  onClick={onBuy}
                  className="btn-secondary py-1 px-3 text-sm"
                >
                  <FiShoppingCart className="mr-1 inline" /> Buy
                </button>
              </motion.div>
            )}
            
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="shadow-3d rounded-lg">
              <a
                href={`https://polygonscan.com/token/${nft.contractAddress}?a=${nft.tokenId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline py-1 px-2 text-sm"
              >
                <FiExternalLink />
              </a>
            </motion.div>
          </div>
          
          {/* Badge for owned NFTs */}
          {isOwned && (
            <div className="absolute top-2 right-2 bg-primary-600 text-white text-xs py-1 px-2 rounded-md z-10">
              Owned
            </div>
          )}
          
          {/* Badge for listed NFTs */}
          {isListed && (
            <div className="absolute top-2 left-2 bg-secondary-600 text-white text-xs py-1 px-2 rounded-md z-10">
              {price} dUSD
            </div>
          )}
        </div>
        
        {/* NFT Info */}
        <div className="p-4">
          <h3 className="text-lg font-bold text-white truncate text-shadow">
            {nft.metadata?.name || `NFT #${nft.tokenId}`}
          </h3>
          
          <div className="flex justify-between items-center mt-2">
            <div className="text-sm text-gray-400">
              {nft.tokenType === 'ERC721' ? 'Unique' : 'Edition'}
            </div>
            
            <div className="text-sm text-gray-400">
              Creator: {nft.creator?.username || formatAddress(nft.creatorId)}
            </div>
          </div>
          
          {/* Attributes preview (if available) */}
          {nft.metadata?.attributes && nft.metadata.attributes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {nft.metadata.attributes.slice(0, 3).map((attr, index) => (
                <div
                  key={index}
                  className="bg-dark-700 rounded-md px-2 py-1 text-xs text-gray-300"
                >
                  <span className="text-gray-400">{attr.trait_type}:</span>{' '}
                  {attr.value.toString()}
                </div>
              ))}
              {nft.metadata.attributes.length > 3 && (
                <div className="bg-dark-700 rounded-md px-2 py-1 text-xs text-gray-300">
                  +{nft.metadata.attributes.length - 3} more
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default NFTCard;